/**
 * Boolean `switch` primitive for the Monitor wizard.
 *
 * Renders a shadcn Switch with the field's label as the trigger. Used for
 * Intercom's `include_closed` (and any future capability boolean toggle).
 *
 * The wizard stores values as primitives in `sourceConfig`; this component
 * coerces undefined → field.default → false so the toggle has a definite
 * state on first render.
 */
import React from 'react';

import { Label } from '../../../../ui/label';
import { Switch } from '../../../../ui/switch';
import type { CapabilityField } from '../../../../../types/importCapabilities';

interface SwitchFieldProps {
  field: CapabilityField;
  value: boolean | undefined;
  onChange: (value: boolean) => void;
}

const resolveDefault = (field: CapabilityField): boolean => {
  return field.default === true;
};

const SwitchField: React.FC<SwitchFieldProps> = ({ field, value, onChange }) => {
  const checked = value ?? resolveDefault(field);
  const fieldId = `field-${field.id}`;

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-0.5">
        <Label htmlFor={fieldId}>
          {field.label || field.id}
          {field.required ? <span className="ml-1 text-red-500">*</span> : null}
        </Label>
        {field.description ? (
          <p className="text-xs text-slate-500">{field.description}</p>
        ) : null}
      </div>
      <Switch id={fieldId} checked={checked} onCheckedChange={onChange} />
    </div>
  );
};

export default SwitchField;
